import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useDispatch } from 'react-redux';
import { useAuth } from '../../../hooks/useAuth';
import {
    aiAdminApi,
    SubsystemDetail,
    SubsystemId,
} from '../../../services/ai-admin-api';
import { api as nestjsApi } from '../../../services/nestjs-base-api';
import { EntityTag } from '../../../services/nestjs-base-api';
type AnyAction = Parameters<ReturnType<typeof useDispatch>>[0];

/**
 * Map each AI subsystem to the RTK Query cache tags whose data is invalidated
 * by a publish. The AI hub uses raw fetch, so without this dispatch the
 * existing /dashboard/* pages (which DO use RTK Query) keep showing stale
 * data until a hard refresh.
 *
 * Be permissive — over-invalidating triggers a refetch that's barely
 * noticeable. Under-invalidating leaves admins thinking publish didn't work.
 */
const SUBSYSTEM_RTK_INVALIDATIONS: Partial<Record<SubsystemId, string[]>> = {
    ai_experts: [EntityTag.AIExperts],
    models_providers: [EntityTag.AIExperts],
    shared_prompt_library: [EntityTag.AIExperts],
    knowledge_base: [EntityTag.AIDocuments],
};

/**
 * Common state machine for every Phase 3 ConfigurePage:
 *  - load detail (current published payload + any open draft)
 *  - hold a working copy of the payload that the page can edit freely
 *  - save-draft / discard-draft / publish actions
 *  - dirty tracking via a structural-equality compare against the source
 *
 * The payload type is opaque (`unknown`) because it differs per subsystem.
 * Each ConfigurePage will cast it to the subsystem-specific shape it
 * understands.
 */

export type DraftEditorState<P = unknown> = {
    detail: SubsystemDetail | null;
    /** Working copy of the payload, editable by the page. */
    payload: P | null;
    /** True when payload differs from the source (draft or published). */
    isDirty: boolean;
    /** True when an in-flight load / save / publish is happening. */
    isBusy: boolean;
    isLoading: boolean;
    isSaving: boolean;
    isPublishing: boolean;
    isDiscarding: boolean;
};

export interface DraftEditorActions<P = unknown> {
    /**
     * Replace the working copy. Accepts either a new value OR a function that
     * receives the previous payload — use the function form for any code path
     * that issues multiple sequential updates, otherwise stale closures cause
     * later updates to clobber earlier ones.
     */
    setPayload: (nextOrFn: P | ((prev: P | null) => P)) => void;
    /** Replaces the working copy with the source — useful for a "Reset" button. */
    resetToSource: () => void;
    saveDraft: (notes?: string) => Promise<void>;
    discardDraft: () => Promise<void>;
    publish: (notes?: string) => Promise<void>;
    reload: () => Promise<void>;
}

export interface UseSubsystemDraftEditorResult<P = unknown> extends DraftEditorState<P>, DraftEditorActions<P> {}

/**
 * Provided by SubsystemPageShell so the shell's header (version badges,
 * draft tag) and the body's Configure component share one editor instance.
 * Without this they'd each maintain their own detail state and the header
 * could go stale after a publish.
 */
const SubsystemEditorContext = createContext<UseSubsystemDraftEditorResult<any> | null>(null);

export const SubsystemEditorProvider = SubsystemEditorContext.Provider;

export function useSubsystemEditorContext<P = unknown>(): UseSubsystemDraftEditorResult<P> {
    const ctx = useContext(SubsystemEditorContext);
    if (!ctx) {
        throw new Error(
            'useSubsystemEditorContext must be used inside a SubsystemPageShell',
        );
    }
    return ctx as UseSubsystemDraftEditorResult<P>;
}

function clone<T>(value: T): T {
    if (value === null || value === undefined) return value;
    return JSON.parse(JSON.stringify(value));
}

function structuralEquals(a: unknown, b: unknown): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

export function useSubsystemDraftEditor<P = unknown>(
    subsystem: SubsystemId,
): UseSubsystemDraftEditorResult<P> {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const dispatch = useDispatch();

    const [detail, setDetail] = useState<SubsystemDetail | null>(null);
    const [payload, setPayloadState] = useState<P | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);

    /**
     * An open draft first, then LIVE state — never the last published payload.
     *
     * Publishing applies the draft's payload to the live tables, so whatever
     * seeds an untouched draft is what a publish writes. Seeding from
     * `publishedPayload` therefore made "publish with no edits" silently
     * revert anything changed outside the Hub: a settings UPDATE run by hand
     * survived until the next publish of that subsystem, then vanished with no
     * diff and no warning. Prod ran a stale Hypothesis element set for five
     * days that way.
     *
     * Seeding from live makes an unmodified publish a true no-op, and leaves
     * the published-vs-live gap visible in the diff instead of erasing it.
     */
    const sourcePayload = useMemo<P | null>(() => {
        if (!detail) return null;
        const source =
            detail.draftPayload ?? detail.livePayload ?? detail.publishedPayload ?? null;
        return source ? (clone(source) as P) : null;
    }, [detail]);

    const isDirty = useMemo(
        () => !structuralEquals(payload, sourcePayload),
        [payload, sourcePayload],
    );

    const reload = useCallback(async () => {
        if (!jwt) return;
        setIsLoading(true);
        try {
            const next = await aiAdminApi.getSubsystem(jwt, subsystem);
            setDetail(next);
            const seed =
                next.draftPayload ?? next.livePayload ?? next.publishedPayload ?? null;
            setPayloadState(seed ? (clone(seed) as P) : null);
        } catch (err: any) {
            message.error(err.message || 'Failed to load subsystem detail');
        } finally {
            setIsLoading(false);
        }
    }, [jwt, subsystem]);

    useEffect(() => {
        reload();
    }, [reload]);

    const setPayload = useCallback(
        (nextOrFn: P | ((prev: P | null) => P)) => {
            if (typeof nextOrFn === 'function') {
                setPayloadState((prev) =>
                    (nextOrFn as (prev: P | null) => P)(prev),
                );
            } else {
                setPayloadState(nextOrFn);
            }
        },
        [],
    );

    const resetToSource = useCallback(() => {
        setPayloadState(sourcePayload ? (clone(sourcePayload) as P) : null);
    }, [sourcePayload]);

    const saveDraft = useCallback(
        async (notes?: string) => {
            if (!payload) return;
            setIsSaving(true);
            try {
                await aiAdminApi.saveDraft(jwt, subsystem, payload, notes);
                message.success('Draft saved.');
                await reload();
            } catch (err: any) {
                message.error(err.message || 'Save draft failed');
            } finally {
                setIsSaving(false);
            }
        },
        [jwt, subsystem, payload, reload],
    );

    const discardDraft = useCallback(async () => {
        setIsDiscarding(true);
        try {
            await aiAdminApi.discardDraft(jwt, subsystem);
            message.success('Draft discarded.');
            await reload();
        } catch (err: any) {
            message.error(err.message || 'Discard draft failed');
        } finally {
            setIsDiscarding(false);
        }
    }, [jwt, subsystem, reload]);

    const publish = useCallback(
        async (notes?: string) => {
            setIsPublishing(true);
            try {
                await aiAdminApi.publishDraft(jwt, subsystem, notes);
                message.success('Published.');
                // Bust the legacy RTK Query caches so /dashboard/* pages don't
                // show stale data after a publish from the AI hub.
                const tags = SUBSYSTEM_RTK_INVALIDATIONS[subsystem] ?? [];
                if (tags.length > 0) {
                    dispatch(
                        nestjsApi.util.invalidateTags(tags as any) as unknown as AnyAction,
                    );
                }
                await reload();
            } catch (err: any) {
                message.error(err.message || 'Publish failed');
            } finally {
                setIsPublishing(false);
            }
        },
        [jwt, subsystem, reload, dispatch],
    );

    const isBusy = isLoading || isSaving || isPublishing || isDiscarding;

    return {
        detail,
        payload,
        isDirty,
        isBusy,
        isLoading,
        isSaving,
        isPublishing,
        isDiscarding,
        setPayload,
        resetToSource,
        saveDraft,
        discardDraft,
        publish,
        reload,
    };
}
